/* eslint-disable no-console */
/* eslint-disable ts/no-explicit-any */
/* eslint-disable ts/no-unsafe-assignment */
/* eslint-disable ts/no-unsafe-argument */

import * as Sentry from '@sentry/react'
import {
  getCurrentEnvironment,
  getEnvironmentConfig,
  isSentryEnabled,
} from '@/shared/config/environment'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerConfig {
  enabled: boolean
  level: LogLevel
  prefix?: string
}

class Logger {
  private config: LoggerConfig
  private timers: Map<string, number> = new Map()

  constructor(config?: Partial<LoggerConfig>) {
    const envConfig = getEnvironmentConfig()

    this.config = {
      enabled: envConfig.enabled,
      level: envConfig.logLevel,
      prefix: '',
      ...config,
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false

    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error']
    const currentLevelIndex = levels.indexOf(this.config.level)
    const messageLevelIndex = levels.indexOf(level)

    return messageLevelIndex >= currentLevelIndex
  }

  private formatMessage(level: LogLevel, message: string, ...args: any[]): [string, ...any[]] {
    const timestamp = new Date().toISOString()
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : ''
    const environment = getCurrentEnvironment()
    const levelEmoji = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
    }

    return [
      `${levelEmoji[level]} ${timestamp} [${environment.toUpperCase()}] ${prefix} ${message}`,
      ...args,
    ]
  }

  private addSentryBreadcrumb(level: LogLevel, message: string, data?: any): void {
    if (!isSentryEnabled()) return

    // Map LogLevel to Sentry's breadcrumb level
    const sentryLevel =
      level === 'debug'
        ? 'debug'
        : level === 'info'
          ? 'info'
          : level === 'warn'
            ? 'warning'
            : 'error'

    const breadcrumb: {
      category: string
      level: 'debug' | 'info' | 'warning' | 'error'
      message: string
      timestamp: number
      data?: Record<string, any>
    } = {
      category: this.config.prefix ? `logger:${this.config.prefix}` : 'logger',
      level: sentryLevel,
      message,
      timestamp: Date.now() / 1000,
    }

    if (data) {
      breadcrumb.data = { data }
    }

    Sentry.addBreadcrumb(breadcrumb)
  }

  debug(message: string, context?: Record<string, any>): void {
    if (this.shouldLog('debug')) {
      console.log(...this.formatMessage('debug', message, context))
      this.addSentryBreadcrumb('debug', message, context)
    }
  }

  info(message: string, context?: Record<string, any>): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('info', message, context))
      this.addSentryBreadcrumb('info', message, context)
    }
  }

  warn(message: string, error?: Error | Record<string, any>, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', message, error, ...args))
      this.addSentryBreadcrumb('warn', message, { error, args })

      // Send warnings to Sentry if it's an Error object
      if (isSentryEnabled() && error instanceof Error) {
        Sentry.captureException(error, {
          level: 'warning',
          extra: {
            message,
            args,
            logger_prefix: this.config.prefix,
          },
        })
      }
    }
  }

  error(message: string, error?: Error | Record<string, any>, ...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', message, error, ...args))
      this.addSentryBreadcrumb('error', message, { error, args })

      // Auto-send errors to Sentry
      if (isSentryEnabled()) {
        if (error instanceof Error) {
          Sentry.captureException(error, {
            level: 'error',
            extra: {
              message,
              args,
              logger_prefix: this.config.prefix,
            },
          })
        } else {
          // If no Error object, create one
          Sentry.captureException(new Error(message), {
            level: 'error',
            extra: {
              error,
              args,
              logger_prefix: this.config.prefix,
            },
          })
        }
      }
    }
  }

  group(label: string): void {
    if (this.config.enabled) {
      console.group(label)
    }
  }

  groupEnd(): void {
    if (this.config.enabled) {
      console.groupEnd()
    }
  }

  table(data: any): void {
    if (this.config.enabled) {
      console.table(data)
    }
  }

  /**
   * Start a performance timer
   * Use timeEnd() with the same label to log the elapsed time
   */
  time(label: string): void {
    if (!this.config.enabled) return

    this.timers.set(label, Date.now())
    console.time(label)

    // Add breadcrumb for timer start
    if (isSentryEnabled()) {
      Sentry.addBreadcrumb({
        category: this.config.prefix ? `logger:${this.config.prefix}:timer` : 'logger:timer',
        message: `Timer started: ${label}`,
        level: 'debug',
        timestamp: Date.now() / 1000,
      })
    }
  }

  /**
   * End a performance timer and log the elapsed time
   * Also sends performance data to Sentry
   */
  timeEnd(label: string): void {
    if (!this.config.enabled) return

    const startTime = this.timers.get(label)
    if (startTime) {
      const duration = Date.now() - startTime
      this.timers.delete(label)

      console.timeEnd(label)
      this.info(`Timer ${label}: ${duration}ms`, { duration })

      // Send performance data to Sentry
      if (isSentryEnabled()) {
        Sentry.addBreadcrumb({
          category: this.config.prefix ? `logger:${this.config.prefix}:timer` : 'logger:timer',
          message: `Timer ended: ${label}`,
          level: 'info',
          data: { duration, label },
          timestamp: Date.now() / 1000,
        })
      }
    } else {
      this.warn(`Timer ${label} was not started`)
    }
  }

  /**
   * Create a child logger with a specific prefix
   * Useful for creating module-specific loggers
   */
  child(prefix: string): Logger {
    const childPrefix = this.config.prefix ? `${this.config.prefix}:${prefix}` : prefix
    return new Logger({ ...this.config, prefix: childPrefix })
  }
}

// Create default logger instances
export const logger = new Logger()
export const apiLogger = new Logger({ prefix: 'API' })
export const routerLogger = new Logger({ prefix: 'ROUTER' })
export const sentryLogger = new Logger({ prefix: 'SENTRY' })

// Export Logger class for custom instances
export { Logger }
export type { LogLevel }
